















































































































































































































































































import Vue, { PropType } from "vue";
import _ from "underscore";
import { ListItem, Nullable, Project, ProjectKind } from "@/types";
import InputNumber from "@/components/inputs/InputNumber.vue";
import InputPercentage from "@/components/inputs/InputPercentage.vue";
import { TranslateResult } from "vue-i18n";
import {
  RETROFIT_INSTALLATION_TYPE,
  isFinishedLock,
  isPastPlannedLock,
  isWorkProject,
  werkprojectFenceSatisfied
} from "@/util/project-style";

export default Vue.extend({
  components: { InputPercentage, InputNumber },
  props: {
    value: {
      type: Object as PropType<Project>
    },
    rules: {
      type: Object,
      default: () => []
    },
    autofocus: {
      type: String,
      default: ""
    },
    isEditing: Boolean
  },

  data() {
    // The werkproject fence judges a change (K1), so a werkproject that predates the fence still
    // saves its other fields. These are what the project was when the dialog opened.
    return {
      loadedKind: (this.value?.kind ?? null) as Nullable<ProjectKind>,
      loadedInstallationType: (this.value?.installationType ??
        null) as Nullable<number>
    };
  },

  mounted() {
    if (!this.value?.zmCustomer && !this.isEditing && !this.isZonmaatUser) {
      this.$set(this.value, "zmCustomer", "wocozon");
    }

    if (this.value?.conversion === null) {
      this.$set(this.value, "conversion", 0);
    }
  },

  computed: {
    isZonmaatUser(): boolean {
      return this.$role("zonmaat_office");
    },
    workProject(): boolean {
      return isWorkProject(this.value);
    },
    locked(): boolean {
      return isFinishedLock(this.value);
    },
    pastPlanned(): boolean {
      return isPastPlannedLock(this.value);
    },
    requiresPlannedFields(): boolean {
      return this.value.status === 0 && !this.workProject;
    },
    installationTypes(): ListItem[] {
      const all = (this.$t(
        "office.dictionaries.installationTypes"
      ) as unknown) as ListItem[];

      if (!this.workProject || this.value.kind !== "installation") return all;

      const kindUnchanged = (this.value.kind ?? null) === this.loadedKind;

      return all.filter(
        option =>
          option.value === RETROFIT_INSTALLATION_TYPE ||
          (kindUnchanged && option.value === this.loadedInstallationType)
      );
    },
    fenceMessages(): string[] {
      if (!this.workProject) return [];

      const changed =
        (this.value.kind ?? null) !== this.loadedKind ||
        this.value.installationType !== this.loadedInstallationType;

      if (
        !changed ||
        werkprojectFenceSatisfied(this.value.kind, this.value.installationType)
      ) {
        return [];
      }

      return [this.$t("office.validation.werkprojectFence").toString()];
    },
    teamsUrl: {
      get(): string | null {
        return this.value.teamsUrl || this.value.defaultWebpageProjects;
      },
      set(val: string) {
        this.$set(this.value, "teamsUrl", val);
      }
    },
    installer(): number | undefined {
      if (!this.value.housingCooperativeId) {
        return undefined;
      }

      const hoco: any = this.housingCooperatives.find(
        i => i.id === this.value.housingCooperativeId
      );

      return hoco?.defaultInstallerId;
    },

    installers(): unknown[] {
      return _.sortBy(
        this.housingCooperative?.installers ||
          this.$store.state.dictionaries.installers.items,
        "name"
      );
    },

    housingCooperatives(): any[] {
      return _.sortBy(
        this.$store.state.dictionaries.housingCooperatives.items,
        "name"
      );
    },

    housingCooperative(): any {
      return this.housingCooperatives.find(
        hoco => this.value.housingCooperativeId === hoco.id
      );
    },

    projectManagers(): unknown[] {
      return _.sortBy(
        this.$store.state.dictionaries.projectManagers.items,
        "name"
      );
    },

    status(): number {
      return this.value.status;
    },

    expectedHouses: {
      get(): number {
        return this.value?.expectedHouses ?? 0;
      },

      set(val: number = 0) {
        if (this.pastPlanned) return;

        if (val < 0) val = 0;

        this.value.expectedHouses = val;
      }
    },
    conversion: {
      get(): number {
        return this.value?.conversion ?? 0;
      },

      set(val: number) {
        if (val > 100) val = 100;
        if (val < 0) val = 0;

        this.value.conversion = val ?? 0;
      }
    },

    zipcodeTwoArea: {
      get(): number {
        return this.value?.zipcodeTwoArea ?? 0;
      },

      set(val: number) {
        this.value.zipcodeTwoArea = val ?? 0;
      }
    },

    expectedInstallations: {
      get(): number {
        if (this.value?.status === 0) {
          return Math.round((this.expectedHouses! / 100) * this.conversion!);
        }

        return Math.round((this.value.housesCount / 100) * this.conversion!);
      },

      set(val: number = 0) {
        if (this.value?.status === 0) {
          this.conversion = val ? (val / this.expectedHouses!) * 100 : 0;
        } else if (this.value.status === 1)
          this.conversion = val ? (val / this.value.housesCount!) * 100 : 0;
      }
    },
    zonmaatCustomers(): any[] {
      const zmCustomers = (this.$t(
        "office.dictionaries.zonmaatCustomers"
      ) as unknown) as any[];

      if (this.isEditing) {
        return zmCustomers;
      } else if (this.$role("zonmaat_office")) {
        return zmCustomers.filter(({ value }) => value !== "wocozon");
      } else {
        return zmCustomers.filter(({ value }) => value === "wocozon");
      }
    }
  },

  methods: {
    setInstaller() {
      this.$set(
        this.value,
        "installerId",
        this.housingCooperative?.defaultInstallerId
      );
    }
  }
});
